import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Admin.css';
import { siteContent } from '../data/content';

function AdminDashboard() {
  const [activeSection, setActiveSection] = useState('overview');
  const [content, setContent] = useState(siteContent);
  // eslint-disable-next-line no-unused-vars
  const [editMode, setEditMode] = useState({});
  const [savedMessage, setSavedMessage] = useState('');

  // Save changes to localStorage (in production, this would save to a database)
  const saveChanges = (section, data) => {
    const updatedContent = { ...content, [section]: data };
    setContent(updatedContent);
    localStorage.setItem('blbc-content', JSON.stringify(updatedContent));
    setSavedMessage('✅ Changes saved successfully!');
    setTimeout(() => setSavedMessage(''), 3000);
  };

  // Church Info Editor
  const ChurchInfoEditor = () => {
    const [info, setInfo] = useState(content.church);

    return (
      <div className="editor-section">
        <h2>Church Information</h2>
        <div className="form-grid">
          <div className="form-group">
            <label>Church Name</label>
            <input
              type="text"
              value={info.name}
              onChange={(e) => setInfo({ ...info, name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Tagline</label>
            <input
              type="text"
              value={info.tagline}
              onChange={(e) => setInfo({ ...info, tagline: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              value={info.phone}
              onChange={(e) => setInfo({ ...info, phone: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={info.email}
              onChange={(e) => setInfo({ ...info, email: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              value={info.address}
              onChange={(e) => setInfo({ ...info, address: e.target.value })}
            />
          </div>
        </div>
        <button className="save-btn" onClick={() => saveChanges('church', info)}>
          Save Church Info
        </button>
      </div>
    );
  };

  // Service Times Editor
  const ServiceTimesEditor = () => {
    const [times, setTimes] = useState(content.serviceTimes);

    const updateTime = (index, field, value) => {
      const updated = [...times];
      updated[index][field] = value;
      setTimes(updated);
    };

    return (
      <div className="editor-section">
        <h2>Service Times</h2>
        {times.map((service, index) => (
          <div key={index} className="service-time-item">
            <input
              type="text"
              value={service.name}
              onChange={(e) => updateTime(index, 'name', e.target.value)}
              placeholder="Service Name"
            />
            <input
              type="text"
              value={service.time}
              onChange={(e) => updateTime(index, 'time', e.target.value)}
              placeholder="Time"
            />
          </div>
        ))}
        <button className="save-btn" onClick={() => saveChanges('serviceTimes', times)}>
          Save Service Times
        </button>
      </div>
    );
  };

  // Sermon Editor
  const SermonEditor = () => {
    const [sermons, setSermons] = useState(content.sermons);
    const [newSermon, setNewSermon] = useState({
      date: '',
      title: '',
      speaker: '',
      description: '',
      icon: '🎤',
      duration: '',
      views: 0
    });

    const addSermon = () => {
      setSermons([newSermon, ...sermons]);
      setNewSermon({
        date: '',
        title: '',
        speaker: '',
        description: '',
        icon: '🎤',
        duration: '',
        views: 0
      });
    };

    const deleteSermon = (index) => {
      const updated = sermons.filter((_, i) => i !== index);
      setSermons(updated);
    };

    return (
      <div className="editor-section">
        <h2>Sermons</h2>
        
        <div className="add-sermon">
          <h3>Add New Sermon</h3>
          <div className="form-grid">
            <input
              type="text"
              placeholder="Date (e.g., February 16, 2026)"
              value={newSermon.date}
              onChange={(e) => setNewSermon({ ...newSermon, date: e.target.value })}
            />
            <input
              type="text"
              placeholder="Title"
              value={newSermon.title}
              onChange={(e) => setNewSermon({ ...newSermon, title: e.target.value })}
            />
            <input
              type="text"
              placeholder="Speaker"
              value={newSermon.speaker}
              onChange={(e) => setNewSermon({ ...newSermon, speaker: e.target.value })}
            />
            <input
              type="text"
              placeholder="Duration (e.g., 42 min)"
              value={newSermon.duration}
              onChange={(e) => setNewSermon({ ...newSermon, duration: e.target.value })}
            />
          </div>
          <textarea
            placeholder="Description"
            value={newSermon.description}
            onChange={(e) => setNewSermon({ ...newSermon, description: e.target.value })}
          />
          <button className="add-btn" onClick={addSermon}>+ Add Sermon</button>
        </div>

        <div className="sermon-list">
          <h3>Current Sermons ({sermons.length})</h3>
          {sermons.map((sermon, index) => (
            <div key={index} className="sermon-item">
              <div className="sermon-info">
                <h4>{sermon.title}</h4>
                <p>{sermon.speaker} • {sermon.date} • {sermon.duration}</p>
              </div>
              <button className="delete-btn" onClick={() => deleteSermon(index)}>Delete</button>
            </div>
          ))}
        </div>

        <button className="save-btn" onClick={() => saveChanges('sermons', sermons)}>
          Save All Sermons
        </button>
      </div>
    );
  };

  // Event Editor
  const EventEditor = () => {
    const [events, setEvents] = useState(content.events);
    const [newEvent, setNewEvent] = useState({
      icon: '📅',
      title: '',
      date: '',
      description: '',
      recurring: false
    });

    const addEvent = () => {
      setEvents([...events, newEvent]);
      setNewEvent({ icon: '📅', title: '', date: '', description: '', recurring: false });
    };

    const deleteEvent = (index) => {
      const updated = events.filter((_, i) => i !== index);
      setEvents(updated);
    };

    return (
      <div className="editor-section">
        <h2>Events</h2>
        
        <div className="add-event">
          <h3>Add New Event</h3>
          <input
            type="text"
            placeholder="Event Title"
            value={newEvent.title}
            onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
          />
          <input
            type="text"
            placeholder="Date/Time"
            value={newEvent.date}
            onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
          />
          <textarea
            placeholder="Description"
            value={newEvent.description}
            onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
          />
          <label>
            <input
              type="checkbox"
              checked={newEvent.recurring}
              onChange={(e) => setNewEvent({ ...newEvent, recurring: e.target.checked })}
            />
            Recurring Event
          </label>
          <button className="add-btn" onClick={addEvent}>+ Add Event</button>
        </div>

        <div className="event-list">
          <h3>Current Events ({events.length})</h3>
          {events.map((event, index) => (
            <div key={index} className="event-item">
              <div>
                <h4>{event.icon} {event.title}</h4>
                <p>{event.date}</p>
              </div>
              <button className="delete-btn" onClick={() => deleteEvent(index)}>Delete</button>
            </div>
          ))}
        </div>

        <button className="save-btn" onClick={() => saveChanges('events', events)}>
          Save All Events
        </button>
      </div>
    );
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>⚙️ Church Website CMS</h1>
        <Link to="/" className="view-site-btn">← View Website</Link>
      </div>

      {savedMessage && <div className="save-message">{savedMessage}</div>}

      <div className="admin-container">
        <aside className="admin-sidebar">
          <nav>
            <button
              className={activeSection === 'overview' ? 'active' : ''}
              onClick={() => setActiveSection('overview')}
            >
              📊 Overview
            </button>
            <button
              className={activeSection === 'church' ? 'active' : ''}
              onClick={() => setActiveSection('church')}
            >
              ⛪ Church Info
            </button>
            <button
              className={activeSection === 'services' ? 'active' : ''}
              onClick={() => setActiveSection('services')}
            >
              🕐 Service Times
            </button>
            <button
              className={activeSection === 'sermons' ? 'active' : ''}
              onClick={() => setActiveSection('sermons')}
            >
              🎤 Sermons
            </button>
            <button
              className={activeSection === 'events' ? 'active' : ''}
              onClick={() => setActiveSection('events')}
            >
              📅 Events
            </button>
          </nav>
        </aside>

        <main className="admin-content">
          {activeSection === 'overview' && (
            <div className="overview">
              <h2>Dashboard Overview</h2>
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>{content.sermons.length}</h3>
                  <p>Total Sermons</p>
                </div>
                <div className="stat-card">
                  <h3>{content.events.length}</h3>
                  <p>Upcoming Events</p>
                </div>
                <div className="stat-card">
                  <h3>{content.ministries.length}</h3>
                  <p>Active Ministries</p>
                </div>
                <div className="stat-card">
                  <h3>4</h3>
                  <p>Leadership Team</p>
                </div>
              </div>
              <div className="quick-actions">
                <h3>Quick Actions</h3>
                <button onClick={() => setActiveSection('sermons')}>+ Add Sermon</button>
                <button onClick={() => setActiveSection('events')}>+ Add Event</button>
                <button onClick={() => setActiveSection('church')}>Edit Church Info</button>
              </div>
            </div>
          )}

          {activeSection === 'church' && <ChurchInfoEditor />}
          {activeSection === 'services' && <ServiceTimesEditor />}
          {activeSection === 'sermons' && <SermonEditor />}
          {activeSection === 'events' && <EventEditor />}
        </main>
      </div>
    </div>
  );
}

export default AdminDashboard;
